import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { ScissorsIcon, PenToolIcon, BookOpenIcon, UsersIcon } from 'lucide-react';
export function ZineWorkshopPage() {
  const {
    currentEra
  } = useTheme();
  // Era-specific styling for the page container
  const getContainerStyles = () => {
    switch (currentEra) {
      case '60s':
        return 'bg-white text-black';
      case '70s':
        return 'bg-amber-50 text-amber-900';
      case '80s':
        return 'bg-indigo-900 text-white';
      case '90s':
        return 'bg-black text-white';
      case '2000s':
        return 'bg-white text-gray-800';
      default:
        return 'bg-black text-white';
    }
  };
  // Era-specific styling for headings
  const getHeadingStyles = () => {
    switch (currentEra) {
      case '60s':
        return 'text-4xl md:text-5xl font-bold uppercase text-black';
      case '70s':
        return 'text-4xl md:text-5xl font-bold uppercase text-amber-800';
      case '80s':
        return 'text-4xl md:text-5xl font-bold uppercase text-cyan-400';
      case '90s':
        return 'text-4xl md:text-5xl font-bold uppercase text-pink-500';
      case '2000s':
        return 'text-4xl md:text-5xl font-bold uppercase text-blue-600';
      default:
        return 'text-4xl md:text-5xl font-bold uppercase text-pink-500';
    }
  };
  // Era-specific styling for section headings
  const getSectionHeadingStyles = () => {
    switch (currentEra) {
      case '60s':
        return 'text-2xl font-bold uppercase text-black border-b-2 border-black pb-2 mb-4';
      case '70s':
        return 'text-2xl font-bold uppercase text-amber-800 border-b-2 border-amber-800 pb-2 mb-4';
      case '80s':
        return 'text-2xl font-bold uppercase text-yellow-400 border-b-2 border-dashed border-pink-500 pb-2 mb-4';
      case '90s':
        return 'text-2xl font-bold uppercase text-cyan-400 border-b-2 border-dashed border-pink-500 pb-2 mb-4';
      case '2000s':
        return 'text-2xl font-bold uppercase text-blue-600 border-b-2 border-blue-600 pb-2 mb-4';
      default:
        return 'text-2xl font-bold uppercase text-cyan-400 border-b-2 border-dashed border-pink-500 pb-2 mb-4';
    }
  };
  // Era-specific styling for the info box
  const getInfoBoxStyles = () => {
    switch (currentEra) {
      case '60s':
        return 'bg-gray-200 border-2 border-black p-4';
      case '70s':
        return 'bg-amber-100 border-2 border-amber-800 p-4 rounded-lg';
      case '80s':
        return 'bg-indigo-800 border-2 border-cyan-400 p-4';
      case '90s':
        return 'bg-gray-900 border-2 border-dashed border-pink-500 p-4';
      case '2000s':
        return 'bg-gray-100 border-2 border-blue-600 p-4 rounded shadow-md';
      default:
        return 'bg-gray-900 border-2 border-dashed border-pink-500 p-4';
    }
  };
  // Era-specific styling for the registration button
  const getButtonStyles = () => {
    switch (currentEra) {
      case '60s':
        return 'bg-black text-white px-6 py-3 font-bold uppercase text-lg';
      case '70s':
        return 'bg-amber-800 text-white px-6 py-3 font-bold uppercase text-lg rounded-full';
      case '80s':
        return 'bg-cyan-400 text-black px-6 py-3 font-bold uppercase text-lg';
      case '90s':
        return 'bg-pink-500 text-black px-6 py-3 font-bold uppercase text-lg';
      case '2000s':
        return 'bg-blue-600 text-white px-6 py-3 font-bold uppercase text-lg rounded shadow hover:bg-blue-700 transition-colors';
      default:
        return 'bg-pink-500 text-black px-6 py-3 font-bold uppercase text-lg';
    }
  };
  // Era-specific examples section
  const getExamplesStyles = () => {
    switch (currentEra) {
      case '60s':
        return 'grid grid-cols-1 md:grid-cols-3 gap-4';
      case '70s':
        return 'grid grid-cols-1 md:grid-cols-3 gap-4';
      case '80s':
        return 'grid grid-cols-1 md:grid-cols-3 gap-4';
      case '90s':
        return 'grid grid-cols-1 md:grid-cols-3 gap-4';
      case '2000s':
        return 'grid grid-cols-1 md:grid-cols-3 gap-4';
      default:
        return 'grid grid-cols-1 md:grid-cols-3 gap-4';
    }
  };
  // Era-specific example box styling
  const getExampleBoxStyles = (index: number) => {
    const baseStyles = 'p-4 relative transform';
    switch (currentEra) {
      case '60s':
        return `${baseStyles} bg-white border-2 border-black rotate-${index % 2 === 0 ? '1' : '-1'}`;
      case '70s':
        return `${baseStyles} bg-amber-100 border-2 border-amber-800 rounded-lg rotate-${index % 2 === 0 ? '1' : '-1'}`;
      case '80s':
        return `${baseStyles} bg-indigo-800 border-2 border-cyan-400 rotate-${index % 2 === 0 ? '1' : '-1'}`;
      case '90s':
        return `${baseStyles} bg-gray-900 border-2 border-dashed border-${index % 3 === 0 ? 'pink-500' : index % 3 === 1 ? 'cyan-400' : 'yellow-400'} rotate-${index % 2 === 0 ? '1' : '-1'}`;
      case '2000s':
        return `${baseStyles} bg-white border-2 border-blue-600 rounded shadow-md rotate-${index % 2 === 0 ? '1' : '-1'}`;
      default:
        return `${baseStyles} bg-gray-900 border-2 border-dashed border-${index % 3 === 0 ? 'pink-500' : index % 3 === 1 ? 'cyan-400' : 'yellow-400'} rotate-${index % 2 === 0 ? '1' : '-1'}`;
    }
  };
  // Zine examples data
  const zineExamples = [{
    title: 'PERSONAL NARRATIVE',
    description: 'Create an autobiographical zine about your experiences and perspective',
    icon: <BookOpenIcon className={`h-8 w-8 ${currentEra === '90s' ? 'text-pink-500' : currentEra === '80s' ? 'text-cyan-400' : currentEra === '70s' ? 'text-amber-800' : currentEra === '60s' ? 'text-black' : 'text-blue-600'}`} data-id="element-236" />
  }, {
    title: 'COLLAGE ART',
    description: 'Cut and paste images from magazines to create visual commentary',
    icon: <ScissorsIcon className={`h-8 w-8 ${currentEra === '90s' ? 'text-cyan-400' : currentEra === '80s' ? 'text-yellow-400' : currentEra === '70s' ? 'text-amber-600' : currentEra === '60s' ? 'text-black' : 'text-orange-500'}`} data-id="element-237" />
  }, {
    title: 'ACTIVISM & COMMUNITY',
    description: 'Spread information about causes and create calls to action',
    icon: <UsersIcon className={`h-8 w-8 ${currentEra === '90s' ? 'text-yellow-400' : currentEra === '80s' ? 'text-pink-500' : currentEra === '70s' ? 'text-amber-700' : currentEra === '60s' ? 'text-black' : 'text-green-500'}`} data-id="element-238" />
  }];
  // Get era-specific year
  const getEraYear = () => {
    switch (currentEra) {
      case '60s':
        return '1964';
      case '70s':
        return '1974';
      case '80s':
        return '1984';
      case '90s':
        return '1994';
      case '2000s':
        return '2004';
      default:
        return '1994';
    }
  };
  return <div className={`min-h-screen ${getContainerStyles()}`} data-id="element-239">
      <div className="max-w-6xl mx-auto px-4 py-12" data-id="element-240">
        {/* Header with geometric elements */}
        <div className="relative mb-12" data-id="element-241">
          {/* Era-specific geometric elements */}
          {currentEra === '90s' && <>
              <div className="absolute -top-4 -left-4 w-24 h-24 bg-pink-500 rotate-12 z-0" data-id="element-242"></div>
              <div className="absolute top-8 right-8 w-16 h-16 bg-cyan-400 rotate-45 z-0" data-id="element-243"></div>
            </>}
          {currentEra === '80s' && <>
              <div className="absolute -top-4 -left-4 w-24 h-24 bg-pink-500 z-0" style={{
            clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)'
          }} data-id="element-244"></div>
              <div className="absolute top-8 right-8 w-16 h-16 bg-cyan-400 z-0" style={{
            clipPath: 'polygon(50% 0%, 100% 38%, 82% 100%, 18% 100%, 0% 38%)'
          }} data-id="element-245"></div>
            </>}
          {currentEra === '70s' && <>
              <div className="absolute -top-4 -left-4 w-24 h-24 bg-amber-800 rounded-full z-0" data-id="element-246"></div>
              <div className="absolute top-8 right-8 w-16 h-16 bg-amber-600 rounded-full z-0" data-id="element-247"></div>
            </>}
          {currentEra === '60s' && <>
              <div className="absolute -top-4 -left-4 w-24 h-24 bg-black rotate-12 z-0" data-id="element-248"></div>
              <div className="absolute top-8 right-8 w-16 h-16 bg-black rotate-45 z-0" data-id="element-249"></div>
            </>}
          {currentEra === '2000s' && <>
              <div className="absolute -top-4 -left-4 w-24 h-24 bg-gradient-to-r from-blue-600 to-orange-500 rounded-lg shadow-lg z-0" data-id="element-250"></div>
              <div className="absolute top-8 right-8 w-16 h-16 bg-gradient-to-r from-orange-500 to-blue-600 rounded-lg shadow-lg z-0" data-id="element-251"></div>
            </>}
          {/* Title */}
          <div className="relative z-10 text-center" data-id="element-252">
            <h1 className={getHeadingStyles()} data-id="element-253">DIY ZINE WORKSHOP</h1>
            <p className="text-xl mt-4 font-bold" data-id="element-254">
              MAKE YOUR VOICE HEARD THROUGH SELF-PUBLISHING
            </p>
          </div>
        </div>
        {/* Workshop info box */}
        <div className={`${getInfoBoxStyles()} mb-12`} data-id="element-255">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-256">
            <div data-id="element-257">
              <h2 className={getSectionHeadingStyles()} data-id="element-258">WHEN & WHERE</h2>
              <ul className="space-y-2" data-id="element-259">
                <li className="flex items-start" data-id="element-260">
                  <span className="font-bold mr-2" data-id="element-261">DATE:</span>
                  <span data-id="element-262">Saturday, July 15, {getEraYear()}</span>
                </li>
                <li className="flex items-start" data-id="element-263">
                  <span className="font-bold mr-2" data-id="element-264">TIME:</span>
                  <span data-id="element-265">2:00 PM - 4:00 PM</span>
                </li>
                <li className="flex items-start" data-id="element-266">
                  <span className="font-bold mr-2" data-id="element-267">LOCATION:</span>
                  <span data-id="element-268">Community Center, 123 Main St, Lower Level</span>
                </li>
                <li className="flex items-start" data-id="element-269">
                  <span className="font-bold mr-2" data-id="element-270">COST:</span>
                  <span data-id="element-271">FREE! All materials provided</span>
                </li>
              </ul>
            </div>
            <div data-id="element-272">
              <h2 className={getSectionHeadingStyles()} data-id="element-273">WHAT TO BRING</h2>
              <ul className="space-y-2" data-id="element-274">
                <li data-id="element-275">• Yourself and your ideas</li>
                <li data-id="element-276">• Any images or text you want to include (optional)</li>
                <li data-id="element-277">• Friends who might be interested</li>
                <li data-id="element-278">• A bag to carry your finished zine home</li>
              </ul>
            </div>
          </div>
        </div>
        {/* What are zines section */}
        <div className="mb-12" data-id="element-279">
          <h2 className={getSectionHeadingStyles()} data-id="element-280">WHAT ARE ZINES?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-281">
            <div className="col-span-2" data-id="element-282">
              <p className="mb-4" data-id="element-283">
                Zines (pronounced "zeens") are self-published,
                small-circulation, independent publications. They're created by
                people who have something to say but don't have access to - or
                interest in - traditional publishing channels.
              </p>
              <p className="mb-4" data-id="element-284">
                Zines have been a critical tool for marginalized voices,
                especially in queer communities, to share stories, spread
                information, and build solidarity outside mainstream media.
              </p>
              <p data-id="element-285">
                From hand-drawn illustrations to cut-and-paste collages,
                photocopied manifestos to hand-stapled poetry collections, zines
                embrace a DIY aesthetic that values authenticity over polish,
                and personal expression over mass appeal.
              </p>
            </div>
            <div className={getInfoBoxStyles()} data-id="element-286">
              <h3 className="font-bold text-lg mb-2 uppercase" data-id="element-287">ZINE HISTORY</h3>
              <p className="text-sm mb-2" data-id="element-288">
                While small press publications have existed for centuries,
                modern zines emerged from science fiction fan communities in the
                1930s.
              </p>
              <p className="text-sm mb-2" data-id="element-289">
                The punk movement of the 1970s embraced zines as a way to
                document the scene outside commercial media.
              </p>
              <p className="text-sm" data-id="element-290">
                In the 1990s, Riot Grrrl and queer punk scenes used zines to
                build feminist and queer community networks, creating spaces for
                voices excluded from mainstream publishing.
              </p>
            </div>
          </div>
        </div>
        {/* Workshop content */}
        <div className="mb-12" data-id="element-291">
          <h2 className={getSectionHeadingStyles()} data-id="element-292">WHAT YOU'LL LEARN</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-293">
            <div data-id="element-294">
              <ul className="space-y-4" data-id="element-295">
                <li className="flex items-start" data-id="element-296">
                  <span className={`inline-block w-4 h-4 mt-1 mr-2 ${currentEra === '90s' ? 'bg-pink-500' : currentEra === '80s' ? 'bg-cyan-400' : currentEra === '70s' ? 'bg-amber-800' : currentEra === '60s' ? 'bg-black' : 'bg-blue-600'}`} data-id="element-297"></span>
                  <span data-id="element-298">Basic zine formats and folding techniques</span>
                </li>
                <li className="flex items-start" data-id="element-299">
                  <span className={`inline-block w-4 h-4 mt-1 mr-2 ${currentEra === '90s' ? 'bg-cyan-400' : currentEra === '80s' ? 'bg-pink-500' : currentEra === '70s' ? 'bg-amber-600' : currentEra === '60s' ? 'bg-black' : 'bg-orange-500'}`} data-id="element-300"></span>
                  <span data-id="element-301">Collage and layout methods</span>
                </li>
                <li className="flex items-start" data-id="element-302">
                  <span className={`inline-block w-4 h-4 mt-1 mr-2 ${currentEra === '90s' ? 'bg-yellow-400' : currentEra === '80s' ? 'bg-yellow-400' : currentEra === '70s' ? 'bg-amber-700' : currentEra === '60s' ? 'bg-black' : 'bg-green-500'}`} data-id="element-303"></span>
                  <span data-id="element-304">Hand-lettering and typography</span>
                </li>
                <li className="flex items-start" data-id="element-305">
                  <span className={`inline-block w-4 h-4 mt-1 mr-2 ${currentEra === '90s' ? 'bg-purple-600' : currentEra === '80s' ? 'bg-purple-600' : currentEra === '70s' ? 'bg-amber-800' : currentEra === '60s' ? 'bg-black' : 'bg-purple-600'}`} data-id="element-306"></span>
                  <span data-id="element-307">Binding and assembly techniques</span>
                </li>
              </ul>
            </div>
            <div data-id="element-308">
              <ul className="space-y-4" data-id="element-309">
                <li className="flex items-start" data-id="element-310">
                  <span className={`inline-block w-4 h-4 mt-1 mr-2 ${currentEra === '90s' ? 'bg-pink-500' : currentEra === '80s' ? 'bg-cyan-400' : currentEra === '70s' ? 'bg-amber-800' : currentEra === '60s' ? 'bg-black' : 'bg-blue-600'}`} data-id="element-311"></span>
                  <span data-id="element-312">Storytelling and content organization</span>
                </li>
                <li className="flex items-start" data-id="element-313">
                  <span className={`inline-block w-4 h-4 mt-1 mr-2 ${currentEra === '90s' ? 'bg-cyan-400' : currentEra === '80s' ? 'bg-pink-500' : currentEra === '70s' ? 'bg-amber-600' : currentEra === '60s' ? 'bg-black' : 'bg-orange-500'}`} data-id="element-314"></span>
                  <span data-id="element-315">Photocopying and reproduction tips</span>
                </li>
                <li className="flex items-start" data-id="element-316">
                  <span className={`inline-block w-4 h-4 mt-1 mr-2 ${currentEra === '90s' ? 'bg-yellow-400' : currentEra === '80s' ? 'bg-yellow-400' : currentEra === '70s' ? 'bg-amber-700' : currentEra === '60s' ? 'bg-black' : 'bg-green-500'}`} data-id="element-317"></span>
                  <span data-id="element-318">Distribution strategies</span>
                </li>
                <li className="flex items-start" data-id="element-319">
                  <span className={`inline-block w-4 h-4 mt-1 mr-2 ${currentEra === '90s' ? 'bg-purple-600' : currentEra === '80s' ? 'bg-purple-600' : currentEra === '70s' ? 'bg-amber-800' : currentEra === '60s' ? 'bg-black' : 'bg-purple-600'}`} data-id="element-320"></span>
                  <span data-id="element-321">Building a community through zines</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
        {/* Materials provided */}
        <div className="mb-12" data-id="element-322">
          <h2 className={getSectionHeadingStyles()} data-id="element-323">MATERIALS PROVIDED</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-324">
            <div className={getInfoBoxStyles()} data-id="element-325">
              <h3 className="font-bold text-center mb-2" data-id="element-326">PAPER</h3>
              <ul className="text-sm space-y-1" data-id="element-327">
                <li data-id="element-328">• Various colors and weights</li>
                <li data-id="element-329">• Recycled options</li>
                <li data-id="element-330">• Card stock for covers</li>
                <li data-id="element-331">• Tracing paper</li>
              </ul>
            </div>
            <div className={getInfoBoxStyles()} data-id="element-332">
              <h3 className="font-bold text-center mb-2" data-id="element-333">TOOLS</h3>
              <ul className="text-sm space-y-1" data-id="element-334">
                <li data-id="element-335">• Scissors and X-acto knives</li>
                <li data-id="element-336">• Glue sticks and tape</li>
                <li data-id="element-337">• Staplers and binding materials</li>
                <li data-id="element-338">• Rulers and cutting mats</li>
              </ul>
            </div>
            <div className={getInfoBoxStyles()} data-id="element-339">
              <h3 className="font-bold text-center mb-2" data-id="element-340">ART SUPPLIES</h3>
              <ul className="text-sm space-y-1" data-id="element-341">
                <li data-id="element-342">• Markers and pens</li>
                <li data-id="element-343">• Stamps and ink pads</li>
                <li data-id="element-344">• Stencils</li>
                <li data-id="element-345">• Colored pencils</li>
              </ul>
            </div>
            <div className={getInfoBoxStyles()} data-id="element-346">
              <h3 className="font-bold text-center mb-2" data-id="element-347">COLLAGE MATERIAL</h3>
              <ul className="text-sm space-y-1" data-id="element-348">
                <li data-id="element-349">• Old magazines and newspapers</li>
                <li data-id="element-350">• Found text</li>
                <li data-id="element-351">• Stickers</li>
                <li data-id="element-352">• Photocopier access</li>
              </ul>
            </div>
          </div>
        </div>
        {/* Examples section */}
        <div className="mb-12" data-id="element-353">
          <h2 className={getSectionHeadingStyles()} data-id="element-354">
            ZINE STYLES YOU CAN CREATE
          </h2>
          <div className={getExamplesStyles()} data-id="element-355">
            {zineExamples.map((example, index) => <div key={index} className={getExampleBoxStyles(index)} data-id="element-356">
                <div className="flex flex-col items-center text-center" data-id="element-357">
                  <div className="mb-4" data-id="element-358">{example.icon}</div>
                  <h3 className="font-bold text-lg mb-2" data-id="element-359">{example.title}</h3>
                  <p className="text-sm" data-id="element-360">{example.description}</p>
                </div>
              </div>)}
          </div>
        </div>
        {/* Workshop facilitator */}
        <div className="mb-12" data-id="element-361">
          <h2 className={getSectionHeadingStyles()} data-id="element-362">WORKSHOP FACILITATOR</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-363">
            <div className={`${getInfoBoxStyles()} col-span-1`} data-id="element-364">
              <div className="w-full h-48 mx-auto bg-gray-300 mb-4 overflow-hidden" data-id="element-365">
                <img src="https://static.wixstatic.com/media/c28b1c_a6bac62889194a3db6303d0a6bd07585~mv2.jpg" alt="Jonathan Valelly" className="w-full h-full object-cover" data-id="element-366" />
              </div>
              <h3 className="font-bold text-lg text-center mb-1" data-id="element-367">
                JONATHAN VALELLY
              </h3>
              <p className="text-sm text-center italic mb-4" data-id="element-368">
                <a href="https://www.valelly.com/" className="underline hover:no-underline" data-id="element-369">
                  www.valelly.com
                </a>
              </p>
            </div>
            <div className="col-span-2" data-id="element-370">
              <p className="mb-4" data-id="element-371">
                Jonathan Valelly is the foremost expert on contemporary zine
                culture in Canada and a socially engaged print artist bringing
                15 years of experience making publications in and with local
                communities.
              </p>
              <p className="mb-4" data-id="element-372">
                With an emphasis on activism, storytelling and Canadian queer
                history, Jonathan's collection includes more than 1000
                publications made by 2SLGBTQIA+ creators, spanning zines of
                every stripe.
              </p>
              <p data-id="element-373">
                "Zines gave me a voice when I felt voiceless. They connected me
                to a community when I felt isolated. Now I want to help others
                discover this powerful medium of self-expression and community
                building."
              </p>
            </div>
          </div>
        </div>
        {/* Pop-up Zine Library */}
        <div className="mb-12" data-id="element-374">
          <h2 className={getSectionHeadingStyles()} data-id="element-375">POP-UP ZINE LIBRARY</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-376">
            <div data-id="element-377">
              <p className="mb-4" data-id="element-378">
                Want to bring queer and trans zine history to your community?
                Ask about doing a pop-up zine library with Jonathan's collection
                of more than 1000 publications made by 2SLGBTQIA+ creators!
              </p>
              <p data-id="element-379">
                Jonathan can bring his extensive archive to your school,
                library, park, studio, church basement or neighbourhood garden.
                The collection is a valuable resource for education,
                inspiration, and community connection.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4" data-id="element-380">
              <div className="w-full h-40 overflow-hidden" data-id="element-381">
                <img src="https://static.wixstatic.com/media/c28b1c_6940b902cd634f00894d45f4e5be50b7~mv2.jpeg" alt="Zine collection sample" className="w-full h-full object-cover" data-id="element-382" />
              </div>
              <div className="w-full h-40 overflow-hidden" data-id="element-383">
                <img src="https://static.wixstatic.com/media/c28b1c_73f6fce6094b4bcf89a0090cb62cea7b~mv2.jpg" alt="Zine workshop in action" className="w-full h-full object-cover" data-id="element-384" />
              </div>
            </div>
          </div>
        </div>
        {/* Additional offerings */}
        <div className="mb-12" data-id="element-385">
          <h2 className={getSectionHeadingStyles()} data-id="element-386">ADDITIONAL OFFERINGS</h2>
          <div className={getInfoBoxStyles()} data-id="element-387">
            <h3 className="font-bold text-lg mb-3 uppercase" data-id="element-388">
              QUEER MEN'S HEALTH & HARM REDUCTION
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-389">
              <div className="col-span-2" data-id="element-390">
                <p className="mb-3 text-sm" data-id="element-391">
                  How do we reduce the harms affecting queer people who use
                  drugs and have sex? The first step is to drop all of the
                  assumptions. Let's back up and listen, centering the
                  importance of pleasure and emphasizing the urgent expertise of
                  our own lived experiences.
                </p>
                <p className="text-sm" data-id="element-392">
                  Jonathan draws on his work with Fagdemic and as a content
                  creator in the HIV/AIDS sector to offer adaptable and
                  responsive conversations about charged and messy topics.
                </p>
              </div>
              <div className="w-full h-32 overflow-hidden" data-id="element-393">
                <img src="https://static.wixstatic.com/media/c28b1c_44dfbb5ca49e4d44bd8dcca4901bcd66~mv2.jpeg" alt="Harm reduction workshop" className="w-full h-full object-cover" data-id="element-394" />
              </div>
            </div>
          </div>
        </div>
        {/* Registration */}
        <div className="text-center mb-12" data-id="element-395">
          <h2 className={getSectionHeadingStyles()} data-id="element-396">JOIN THE WORKSHOP</h2>
          <p className="mb-6" data-id="element-397">
            Space is limited to 20 participants. Pre-registration recommended
            but walk-ins welcome if space allows.
          </p>
          <button className={getButtonStyles()} data-id="element-398">REGISTER NOW</button>
          <p className="mt-4 text-sm" data-id="element-399">
            Questions? Contact Jonathan at jvalelly@gmail.com
          </p>
        </div>
        {/* Footer note */}
        <div className="text-center text-sm italic" data-id="element-400">
          <p data-id="element-401">
            This workshop is made possible through community donations and
            volunteer efforts.
            <br data-id="element-402" />
            All zines created will be eligible for inclusion in the Community
            Zine Library.
          </p>
        </div>
      </div>
    </div>;
}